import React from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import styles from "./date.module.scss";
import KoIcon from "@kwapio/icons";
import KoTooltips from "packages/tooltips";

dayjs.extend(utc);

const CustomDatePicker = ({
  onChange,
  intervalStart,
  dateFormat = "DD-MMM-YYYY",
  intervalEnd,
  holidays,
  showHoliday,
  userLeave = true,
  picker,
  companyLeave = true,
  value,
  presets
}) => {
  const startDate = intervalStart ? dayjs.utc(intervalStart).startOf("day") : null;
  const endDate = intervalEnd ? dayjs.utc(intervalEnd).startOf("day") : null;

  const generateDateRange = (start, end) => {
    const startDate = dayjs.utc(start).startOf("day");
    const endDate = dayjs.utc(end).startOf("day");
    const dates = [];
    for (
      let date = startDate;
      date.isBefore(endDate) || date.isSame(endDate, "day");
      date = date.add(1, "day")
    ) {
      dates.push({ date: date.format("YYYY-MM-DD"), name: null });
    }
    return dates;
  };

  // Build holidays list
  const holidaysList = [];
  if (holidays) {
    if (companyLeave && holidays?.orgLeave) {
      holidaysList.push(
        ...holidays.orgLeave.map((holiday) => ({
          date: dayjs.utc(holiday.date).format("YYYY-MM-DD"),
          name: holiday.title || "Holiday"
        }))
      );
    }

    if (userLeave && holidays?.userLeave) {
      holidaysList.push(
        ...holidays.userLeave.flatMap((leave) =>
          generateDateRange(leave.start, leave.end).map((dateObj) => ({
            ...dateObj,
            name: leave.name || "User Leave"
          }))
        )
      );
    }
  }

  const weekOff =
    holidays?.weekOff?.filter((w) => !w.workDay) ?? [
      { weekDay: "Sunday", workDay: false },
      { weekDay: "Saturday", workDay: false }
    ];

  const weekOffDays = weekOff.map((w) =>
    [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday"
    ].indexOf(w.weekDay)
  );

  const selectedValue =
    value && value !== "0001-01-01T00:00:00Z"
      ? dayjs.utc(value)
      : startDate;

  const disabledDate = (current) => {
    if (!current || !showHoliday) return false;

    const isOutOfRange =
      (startDate && current.isBefore(startDate, "day")) ||
      (endDate && current.isAfter(endDate, "day"));

    const isHoliday = holidaysList.some((holiday) =>
      current.isSame(dayjs.utc(holiday.date), "day")
    );

    const isWeekend = weekOffDays.includes(current.day());

    return isOutOfRange || isHoliday || isWeekend;
  };

  const cellRender = (current, info) => {
    const holiday = holidaysList.find((h) =>
      current.isSame(dayjs.utc(h.date), "day")
    );
    const isWeekend = weekOffDays.includes(current.day());

    if (info.type === "date") {
      return (
        <div
          className={`ant-picker-cell-inner ${
            showHoliday && (holiday || isWeekend) ? styles.holiday : ""
          }`}
        >
          {current.date()}
          {showHoliday && (holiday || isWeekend) && (
            <KoTooltips title={holiday ? holiday.name : "Weekend"}>
              <div className={styles.holidayIcon}>
                <KoIcon name="PlaneOutlined" />
              </div>
            </KoTooltips>
          )}
        </div>
      );
    }
    return info.originNode;
  };

  // ✅ Final timezone-safe handler — no day shift
  const handleDateChange = (date) => {
    if (date) {
      const localDate = dayjs(date).format("YYYY-MM-DD");
      const isoDate = `${localDate}T00:00:00Z`; // fixed UTC midnight for same calendar day
      onChange?.(isoDate);
    } else {
      onChange?.(null);
    }
  };

  return (
    <DatePicker
      format={dateFormat}
      presets={presets}
      disabledDate={disabledDate}
      cellRender={cellRender}
      onChange={handleDateChange}
      picker={picker || "date"}
      value={selectedValue}
    />
  );
};

export default CustomDatePicker;
